import { useCallback, useState } from 'react';

import { GridApi } from 'ag-grid-community';

import { useFormulaCloneManager } from '../../../components/formulas/useFormulaCloneAction';

import useFormulaDraft from '../../../hooks/formulas/useFormulaDraft';
import useHandleQueryParams from '../../../hooks/helper/useHandleQueryParams';
import { useModalState } from '../../../hooks/helper/useModalState';

import { FormulaDetailModel } from '../../../models/formula.model';

import { useRefocusGrid } from './useRefocusGrid';

export function useFormulasCatalogState() {
    const [, setQueryParams, removeQueryParams] = useHandleQueryParams();
    const [activeFormula, setActiveFormula] =
        useState<FormulaDetailModel | null>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const refocusGrid = useRefocusGrid(gridApi);

    const drawer = {
        activeFormula,
        isOpen: Boolean(activeFormula),
        open: (formula: FormulaDetailModel) => {
            setQueryParams({ activeFormulaId: formula.id.toString() });
            setActiveFormula((prev) => {
                if (prev?.id === formula.id) return prev; // already open

                if (gridApi) {
                    gridApi.deselectAll();
                    const node = gridApi
                        .getRenderedNodes()
                        .find((n) => n.data?.id === formula.id);
                    if (node && node.rowIndex != null) {
                        node.setSelected(true);
                        const firstCol = gridApi.getColumns()?.[0]?.getColId();
                        if (firstCol) {
                            gridApi.setFocusedCell(node.rowIndex, firstCol);
                        }
                    }
                }

                return formula;
            });
        },
        close: () => {
            removeQueryParams(['activeFormulaId', 'messageContentId']);
            if (gridApi) {
                const node = gridApi.getSelectedNodes()[0];
                if (node) {
                    gridApi.clearFocusedCell();
                    node.setSelected(false);
                }
            }
            setActiveFormula(null);
        },
    };

    const testMessage = useModalState();
    const draftFormula = useFormulaDraft(activeFormula?.id as number);
    const editModalState = useModalState();

    const editModal = {
        ...editModalState,
        editLive: useCallback(() => {
            draftFormula.mutate({ is_in_edit_mode: true });
            editModalState.close();
        }, [draftFormula, editModalState]),
    };

    const [cloningFormulaId, setCloningFormulaId] = useState<number | null>(
        null,
    );

    const { submitClone, isClonePending } = useFormulaCloneManager(
        cloningFormulaId,
        () => setCloningFormulaId(null),
    );

    const cloneModal = {
        cloningFormulaId,
        select: (id: number) => setCloningFormulaId(id),
        clear: () => setCloningFormulaId(null),
        isPending: isClonePending,
        submit: submitClone,
    };
    const timeSlotsModal = useModalState();

    return {
        drawer,
        testMessage,
        editModal,
        cloneModal,
        timeSlotsModal,
        refocusGrid,
        setGridApi,
        gridApi,
    };
}
